import { UseFormReturn } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { IntakeFormData } from "./IntakeFormSchema";

interface SocialSectionProps {
  form: UseFormReturn<IntakeFormData>;
}

const HOBBIES_OPTIONS = [
  { id: "reading", label: "Reading" },
  { id: "gardening", label: "Gardening" },
  { id: "cooking", label: "Cooking/Baking" },
  { id: "crafts", label: "Arts & Crafts" },
  { id: "music", label: "Music (listening/playing)" },
  { id: "games", label: "Games (cards, puzzles, board games)" },
  { id: "tv_movies", label: "TV/Movies" },
  { id: "exercise", label: "Exercise/Walking" },
  { id: "pets", label: "Pet Care" },
  { id: "fishing", label: "Fishing" },
  { id: "travel", label: "Travel" },
  { id: "volunteering", label: "Volunteering" },
];

const SOCIAL_ACTIVITIES_OPTIONS = [
  { id: "family_visits", label: "Regular family visits" },
  { id: "church", label: "Church/Religious activities" },
  { id: "community_groups", label: "Community groups/clubs" },
  { id: "phone_calls", label: "Regular phone calls with loved ones" },
  { id: "video_calls", label: "Video calls (FaceTime, Zoom)" },
  { id: "senior_center", label: "Senior center activities" },
  { id: "support_groups", label: "Support groups" },
  { id: "meals_with_others", label: "Shared meals with others" },
];

const LIVING_SITUATIONS = [
  { value: "alone", label: "Lives alone" },
  { value: "with_spouse", label: "Lives with spouse/partner" },
  { value: "with_family", label: "Lives with family members" },
  { value: "assisted_living", label: "Assisted living facility" },
  { value: "nursing_home", label: "Nursing home/Skilled nursing" },
  { value: "group_home", label: "Group home" },
];

const SOCIAL_SUPPORT_LEVELS = [
  { value: "strong", label: "Strong support network" },
  { value: "moderate", label: "Moderate support" },
  { value: "limited", label: "Limited support" },
  { value: "isolated", label: "Socially isolated" },
];

const COMMUNICATION_STYLES = [
  { value: "phone", label: "Phone calls" },
  { value: "video", label: "Video calls" },
  { value: "text", label: "Text/SMS" },
  { value: "email", label: "Email" },
  { value: "in_person", label: "In-person visits only" },
];

export function SocialSection({ form }: SocialSectionProps) {
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-foreground border-b pb-2">
        Social & Lifestyle Information
      </h3>
      <p className="text-sm text-muted-foreground">
        Help us understand your social connections and interests to provide personalized care.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          control={form.control}
          name="living_situation"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Living Situation</FormLabel>
              <Select onValueChange={field.onChange} value={field.value || ""}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select living situation" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {LIVING_SITUATIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="social_support_level"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Social Support Level</FormLabel>
              <Select onValueChange={field.onChange} value={field.value || ""}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select support level" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {SOCIAL_SUPPORT_LEVELS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="preferred_communication_style"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Preferred Communication Method</FormLabel>
            <Select onValueChange={field.onChange} value={field.value || ""}>
              <FormControl>
                <SelectTrigger className="w-full md:w-1/2">
                  <SelectValue placeholder="How do you prefer to be contacted?" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {COMMUNICATION_STYLES.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="hobbies"
        render={() => (
          <FormItem>
            <FormLabel>Hobbies & Interests</FormLabel>
            <p className="text-sm text-muted-foreground mb-2">
              Select all that apply
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {HOBBIES_OPTIONS.map((hobby) => (
                <FormField
                  key={hobby.id}
                  control={form.control}
                  name="hobbies"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value?.includes(hobby.id)}
                          onCheckedChange={(checked) => {
                            const current = field.value || [];
                            if (checked) {
                              field.onChange([...current, hobby.id]);
                            } else {
                              field.onChange(
                                current.filter((v) => v !== hobby.id)
                              );
                            }
                          }}
                        />
                      </FormControl>
                      <FormLabel className="font-normal text-sm cursor-pointer">
                        {hobby.label}
                      </FormLabel>
                    </FormItem>
                  )}
                />
              ))}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="social_activities"
        render={() => (
          <FormItem>
            <FormLabel>Social Activities</FormLabel>
            <p className="text-sm text-muted-foreground mb-2">
              Select activities you regularly participate in
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {SOCIAL_ACTIVITIES_OPTIONS.map((activity) => (
                <FormField
                  key={activity.id}
                  control={form.control}
                  name="social_activities"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value?.includes(activity.id)}
                          onCheckedChange={(checked) => {
                            const current = field.value || [];
                            if (checked) {
                              field.onChange([...current, activity.id]);
                            } else {
                              field.onChange(
                                current.filter((v) => v !== activity.id)
                              );
                            }
                          }}
                        />
                      </FormControl>
                      <FormLabel className="font-normal text-sm cursor-pointer">
                        {activity.label}
                      </FormLabel>
                    </FormItem>
                  )}
                />
              ))}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="interests_notes"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Additional Notes</FormLabel>
            <FormControl>
              <Textarea
                placeholder="Tell us more about your interests, hobbies, or social connections that help you stay engaged..."
                className="min-h-[100px]"
                {...field}
                value={field.value || ""}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
